// Data for the mock HMI drawn on this page. It is a made-up compressor panel,
// not a real device screen: boxes are in its own 1280 × 800 pixel space.

export const W = 1280;
export const H = 800;
export const FULL = [0, 0, W, H];

export const STATE_IDS = ['ready', 'running', 'fault'];

export const HMI_STATE = {
    ready: {
        status: 'READY', statusColor: '#3B82F6',
        pressure: '1.02', pump: 'Off', fan: 'Off', auto: true, pumpRpm: '0', fanRpm: '0',
        mode: 'UNLOAD MODE', load: 'unload', alarm: null, alarms: 2,
        motor: 'Stopped', windingA: '32.4', inlet: '15.0', outlet: '15.2', hours: '1,284',
    },
    running: {
        status: 'RUNNING', statusColor: '#22C55E',
        pressure: '38.00', pump: 'On', fan: 'On', auto: true, pumpRpm: '1450', fanRpm: '960',
        mode: 'LOAD MODE', load: 'full', alarm: null, alarms: 1,
        motor: 'Running', windingA: '68.5', inlet: '15.0', outlet: '22.4', hours: '1,286',
    },
    fault: {
        status: 'FAULT', statusColor: '#EF4444',
        pressure: '0.00', pump: 'Off', fan: 'Off', auto: false, pumpRpm: '0', fanRpm: '0',
        mode: 'UNLOAD MODE', load: 'unload', alarm: 'Emergency stop tripped', alarms: 5,
        motor: 'Stopped', windingA: '74.1', inlet: '15.0', outlet: '19.8', hours: '1,286',
    },
};

// Library entries. `anchor` is the part matched on the screenshot (it never
// changes with state); `box` is the region handed to the model.
//   kind 'component'                  the whole card, anchored on its title
//   kind 'item', anchorFrom 'row'     one row, anchored on the row name
//   kind 'item', anchorFrom 'parent'  changes its whole look or shares its name,
//                                     so it is anchored on the card title
const CARD_TITLES = {
    'overview.pressure': [36, 98, 168, 26],
    'overview.loading': [422, 98, 74, 26],
    'overview.latest_alarms': [808, 98, 124, 26],
    'overview.circulation_pump': [36, 354, 146, 26],
    'overview.tower_fan': [422, 354, 92, 26],
    'overview.motor_winding': [808, 354, 172, 26],
    'overview.compressor_motor': [36, 570, 158, 26],
    'overview.cooling_water': [808, 570, 122, 26],
};

const CARDS = {
    'overview.pressure': [24, 88, 370, 240],
    'overview.loading': [410, 88, 370, 240],
    'overview.latest_alarms': [796, 88, 396, 240],
    'overview.circulation_pump': [24, 344, 370, 200],
    'overview.tower_fan': [410, 344, 370, 200],
    'overview.motor_winding': [796, 344, 396, 200],
    'overview.compressor_motor': [24, 560, 756, 152],
    'overview.cooling_water': [796, 560, 396, 152],
};

const card = (name) => ({ name, kind: 'component', anchorFrom: 'title', box: CARDS[name], anchor: CARD_TITLES[name], sample: CARDS[name] });
const row = (name, box, anchor) => ({ name, kind: 'item', anchorFrom: 'row', box, anchor, sample: box });
const inCard = (name, box, parent) => ({
    name, kind: 'item', anchorFrom: 'parent', parent, box, anchor: CARD_TITLES[parent], sample: CARDS[parent],
});

export const TARGETS = [
    row('header.status', [16, 14, 176, 36], [20, 22, 58, 20]),
    {
        name: 'sidebar.alarms', kind: 'component', anchorFrom: 'icon',
        box: [1224, 74, 48, 60], anchor: [1228, 98, 40, 34], sample: [1224, 74, 48, 60],
    },
    card('overview.pressure'),
    inCard('overview.pressure_value', [36, 136, 344, 86], 'overview.pressure'),
    row('overview.pressure_sp', [36, 262, 160, 28], [38, 266, 26, 20]),
    row('overview.pressure_hi', [206, 262, 172, 28], [208, 266, 24, 20]),
    card('overview.loading'),
    inCard('overview.loading_mode', [422, 140, 346, 42], 'overview.loading'),
    inCard('overview.full_load', [424, 234, 110, 48], 'overview.loading'),
    inCard('overview.half_load', [540, 234, 110, 48], 'overview.loading'),
    inCard('overview.unload', [656, 234, 110, 48], 'overview.loading'),
    card('overview.latest_alarms'),
    inCard('overview.first_alarm', [810, 138, 368, 44], 'overview.latest_alarms'),
    card('overview.circulation_pump'),
    inCard('overview.pump_status', [310, 352, 72, 34], 'overview.circulation_pump'),
    inCard('overview.pump_auto_mode', [36, 404, 346, 36], 'overview.circulation_pump'),
    card('overview.tower_fan'),
    inCard('overview.fan_status', [696, 352, 72, 34], 'overview.tower_fan'),
    inCard('overview.fan_auto_mode', [422, 404, 346, 36], 'overview.tower_fan'),
    card('overview.motor_winding'),
    row('overview.winding_a', [808, 404, 372, 36], [812, 410, 66, 24]),
    row('overview.winding_warn', [808, 448, 372, 36], [812, 454, 48, 24]),
    row('overview.winding_trip', [808, 492, 372, 36], [812, 498, 42, 24]),
    card('overview.compressor_motor'),
    inCard('overview.compressor_status', [36, 604, 240, 48], 'overview.compressor_motor'),
    card('overview.cooling_water'),
    row('overview.cooling_inlet', [808, 608, 372, 32], [812, 612, 44, 24]),
];

export const TARGET = Object.fromEntries(TARGETS.map((t) => [t.name, t]));

// Deterministic "match score" so the table looks the same every time:
// 1.000 on the reference screenshot, 0.962~0.998 on screenshots in other states.
export function scoreFor(name, state) {
    if (state === 'ready') return 1;
    let h = 2166136261;
    for (const c of name + state) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
    return 0.962 + (h % 37) / 1000;
}

export function pad([x, y, w, h], p) {
    return [x - p, y - p, w + 2 * p, h + 2 * p];
}

// Widen a box to the frame's 16:10 aspect so zooming into it fills the frame.
export function fitAspect([x, y, w, h], aspect = W / H) {
    if (w / h > aspect) {
        const nh = w / aspect;
        return [x, y - (nh - h) / 2, w, nh];
    }
    const nw = h * aspect;
    return [x - (nw - w) / 2, y, nw, h];
}

// Python-style float literal: 38 → "38.0", 1.02 → "1.02".
export function pyFloat(v) {
    return Number.isInteger(v) ? v.toFixed(1) : String(v);
}
